import {
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    IconButton,
} from '@material-ui/core';
import {
    CloudCircle,
    Delete,
    DragIndicator,
    Edit,
    OfflineBolt,
} from '@material-ui/icons';
import ConditionallyRender from '../../../common/ConditionallyRender';

import { IEnvironment } from '../../../../interfaces/environments';
import React, { useContext, useRef } from 'react';
import AccessContext from '../../../../contexts/AccessContext';
import {
    DELETE_ENVIRONMENT,
    UPDATE_ENVIRONMENT,
} from '../../../AccessProvider/permissions';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { XYCoord } from 'dnd-core';

interface IEnvironmentListItemProps {
    env: IEnvironment;
    setSelectedEnv: React.Dispatch<React.SetStateAction<IEnvironment>>;
    setDeldialogue: React.Dispatch<React.SetStateAction<boolean>>;
    setEditEnvironment: React.Dispatch<React.SetStateAction<boolean>>;
    setToggleDialog: React.Dispatch<React.SetStateAction<boolean>>;
    index: number;
    moveListItem: (dragIndex: number, hoverIndex: number) => IEnvironment[];
    moveListItemApi: (dragIndex: number, hoverIndex: number) => Promise<void>;
}

interface DragItem {
    index: number;
    id: string;
    type: string;
}

const EnvironmentListItem = ({
    env,
    setSelectedEnv,
    setDeldialogue,
    index,
    moveListItem,
    moveListItemApi,
    setToggleDialog,
    setEditEnvironment,
}: IEnvironmentListItemProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const ACCEPT_TYPE = 'LIST_ITEM';
    const [{ isDragging }, drag] = useDrag({
        type: ACCEPT_TYPE,
        item: () => {
            return { env, index };
        },
        collect: (monitor: any) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const [{ handlerId }, drop] = useDrop({
        accept: ACCEPT_TYPE,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        drop(item: DragItem, monitor: DropTargetMonitor) {
            const dragIndex = item.index;
            const hoverIndex = index;
            moveListItemApi(dragIndex, hoverIndex);
        },
        hover(item: DragItem, monitor: DropTargetMonitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            const hoverMiddleY =
                (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            const clientOffset = monitor.getClientOffset();

            const hoverClientY =
                (clientOffset as XYCoord).y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveListItem(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const opacity = isDragging ? 0 : 1;
    drag(drop(ref));

    const { hasAccess } = useContext(AccessContext);
    const tooltipText = env.enabled ? 'Disable' : 'Enable';

    return (
        <ListItem
            style={{ position: 'relative', opacity }}
            ref={ref}
            data-handler-id={handlerId}
        >
            <ListItemIcon>
                <CloudCircle />
            </ListItemIcon>
            <ListItemText
                primary={
                    <>
                        <strong>{env.name}</strong>
                        <ConditionallyRender
                            condition={!env.enabled}
                            show={
                                <span
                                    style={{
                                        padding: '0.2rem',
                                        borderRadius: '5px',
                                        marginLeft: '0.5rem',
                                        backgroundColor: '#000',
                                        color: '#fff',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    disabled
                                </span>
                            }
                        />
                    </>
                }
            />

            <Tooltip title="Drag to reorder">
                <IconButton>
                    <DragIndicator />
                </IconButton>
            </Tooltip>
            <ConditionallyRender
                condition={hasAccess(UPDATE_ENVIRONMENT)}
                show={
                    <Tooltip title={`${tooltipText} environment`}>
                        <IconButton
                            aria-label="disable"
                            disabled={env.protected}
                            onClick={() => {
                                setSelectedEnv(env);
                                setToggleDialog(prev => !prev);
                            }}
                        >
                            <OfflineBolt />
                        </IconButton>
                    </Tooltip>
                }
            />
            <ConditionallyRender
                condition={hasAccess(UPDATE_ENVIRONMENT)}
                show={
                    <Tooltip title="Update environment">
                        <IconButton
                            aria-label="update"
                            disabled={env.protected}
                            onClick={() => {
                                setSelectedEnv(env);
                                setEditEnvironment(prev => !prev);
                            }}
                        >
                            <Edit />
                        </IconButton>
                    </Tooltip>
                }
            />
            <ConditionallyRender
                condition={hasAccess(DELETE_ENVIRONMENT)}
                show={
                    <Tooltip title="Delete environment">
                        <IconButton
                            aria-label="delete"
                            disabled={env.protected}
                            onClick={() => {
                                setDeldialogue(true);
                                setSelectedEnv(env);
                            }}
                        >
                            <Delete />
                        </IconButton>
                    </Tooltip>
                }
            />
        </ListItem>
    );
};

export default EnvironmentListItem;
